// Home assistant (RF-CHAT-01, AC-39/92, Task 12): the Home "Ask" mode is the same conversation component as the
// assistant panel (§3.8) — no second chat UI. Organization-level questions, answered from the projects the
// signed-in person can access; the session is private to its owner (`projectId: null`).
// D36 section B: the Home chat's composer controls — project (or a new one), agent, integrations as context hints and
// the working mode — travel in every turn's context; picking a project talks in that project's session. The choice is
// remembered per browser (ids and the mode only).
import { useNavigate } from "react-router";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { ASSISTANT_MODES, DEFAULT_ASSISTANT_MODE, type AssistantMode, type ProjectSummary } from "@maestros/contracts";
import { useI18n } from "../../i18n/index.tsx";
import { readLocal, writeLocal } from "../../lib/storage.ts";
import { AssistantConversation } from "../assistant/AssistantConversation.tsx";
import { HomeControls, type HomeChatOptions } from "../composer/HomeControls.tsx";
import { slideComposer } from "./composerSlide.ts";
import { conversationTitleFrom } from "./conversationTitle.ts";

const OPTIONS_KEY = "homeChat.options";
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function loadHomeChatOptions(): HomeChatOptions {
  const stored = readLocal<Partial<HomeChatOptions> | null>(OPTIONS_KEY, null);
  const uuid = (v: unknown): string | null => (typeof v === "string" && UUID.test(v) ? v : null);
  return {
    projectId: uuid(stored?.projectId),
    agentId: uuid(stored?.agentId),
    integrationIds: Array.isArray(stored?.integrationIds) ? stored.integrationIds.flatMap((v) => uuid(v) ?? []).slice(0, 10) : [],
    mode: ASSISTANT_MODES.includes(stored?.mode as AssistantMode) ? (stored?.mode as AssistantMode) : DEFAULT_ASSISTANT_MODE,
  };
}

export function HomeAsk({
  projects = [],
  projectsLoaded = false,
  onNewProject,
  onConversationActive,
  onTitle,
  sessionId,
  sessionProjectId,
  onSessionChanged,
}: {
  projects?: readonly Pick<ProjectSummary, "id" | "name">[];
  projectsLoaded?: boolean;
  onNewProject?: () => void;
  /** The first question was sent (true) or the conversation was cleared (false): Home switches its view. */
  onConversationActive?: (active: boolean) => void;
  /** The conversation's title once its first answer arrived (a fallback when none can be made); null when cleared. */
  onTitle?: (title: string | null) => void;
  sessionId?: string;
  sessionProjectId?: string | null;
  onSessionChanged?: (id: string | null) => void;
}) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [options, setOptions] = useState<HomeChatOptions>(() => ({ ...loadHomeChatOptions(), ...(sessionId ? { projectId: sessionProjectId ?? null } : {}) }));
  const agentRef = useRef<HTMLSelectElement>(null);
  const modeRef = useRef<HTMLSelectElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(false);
  /** The composer's top before the view switched (captured while the layout is still the centered one). */
  const fromTop = useRef<number | null>(null);

  // AC-1/AC-3: a title made from the first question; one that cannot be made (empty/unreadable text) shows the
  // fallback title and never blocks the conversation. Only `/clear` (null) resets the header to its default.
  const onFirstExchange = (question: string | null) => {
    if (question === null) onTitle?.(null);
    else onTitle?.(conversationTitleFrom(question) ?? t("home.askFallbackTitle"));
  };

  const composerEl = () => rootRef.current?.querySelector<HTMLElement>(".assistant-composer") ?? null;
  const conversationActive = (next: boolean) => {
    fromTop.current = next && !active ? (composerEl()?.getBoundingClientRect().top ?? null) : null;
    setActive(next);
    onConversationActive?.(next);
  };
  // After the layout switched: move the composer back to where it was, then let it slide to the bottom.
  useLayoutEffect(() => {
    const from = fromTop.current;
    fromTop.current = null;
    const el = composerEl();
    if (active && from !== null && el) slideComposer(el, from);
  }, [active]);

  useEffect(() => {
    writeLocal(OPTIONS_KEY, options);
    // Selecting another scope opens its conversation rather than reusing an incompatible fixed session.
    if (sessionId && options.projectId !== (sessionProjectId ?? null)) navigate("/");
  }, [options, sessionId, sessionProjectId, navigate]);
  // A remembered project that is no longer listed falls back to "All projects".
  useEffect(() => {
    if (!sessionId && projectsLoaded && options.projectId !== null && !projects.some((p) => p.id === options.projectId)) setOptions((o) => ({ ...o, projectId: null }));
  }, [projects, projectsLoaded, options.projectId, sessionId]);

  return (
    <div className="home-ask stack" data-testid="home-ask" ref={rootRef}>
      {/* Fix round 1 (minor): the owner-only privacy note from the old Home Ask composer, restored — the
          assistant panel's own header ("All your projects") never states this on its own. */}
      <p className="subtle" style={{ fontSize: "var(--text-xs)" }}>
        {t("home.askPrivate")}
      </p>
      <AssistantConversation
        key={options.projectId ?? "home"}
        projectId={options.projectId}
        {...(sessionId ? { sessionId } : {})}
        onSessionChanged={onSessionChanged}
        compact
        onConversationActive={conversationActive}
        onFirstExchange={onFirstExchange}
        composerContext={{
          mode: options.mode,
          ...(options.agentId ? { agent_profile_id: options.agentId } : {}),
          ...(options.integrationIds.length > 0 ? { integration_ids: options.integrationIds } : {}),
        }}
        onPickControl={(which) => {
          (which === "agent" ? agentRef : modeRef).current?.focus();
          return true;
        }}
        controls={<HomeControls value={options} onChange={setOptions} projects={projects} onNewProject={() => onNewProject?.()} agentRef={agentRef} modeRef={modeRef} />}
      />
      <p className="subtle home-mode-hint" data-testid="home-mode-hint">
        {t(`composer.mode.${options.mode}`)}: {t(`composer.modeHint.${options.mode}`)}
      </p>
    </div>
  );
}
